"use client";

import { useWebsiteHomeHref } from "@/hooks/use-website-home-href";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import { useAuthStore } from "@/stores/auth-store";
import Image from "next/image";
import { getRedirectFromParamsOrStorage, clearRedirectUrl } from "@/lib/utils/redirect-storage";
import { isAndroid } from "@/lib/utils";
import {
  alignServerAuthCookiesBeforeAppEntry,
  clearClientAuthIfServerSaysSignedOut,
  logAuthDecision,
} from "@/lib/auth/verify-web-cookie-session";
import { useTranslations } from "@/hooks/use-translations";

export default function AuthLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const homeHref = useWebsiteHomeHref();
  const { t } = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const { user, isAuthenticated, syncSession } = useAuthStore();
  const [isAndroidPlatform, setIsAndroidPlatform] = useState(false);

  useEffect(() => {
    setIsAndroidPlatform(isAndroid());
  }, []);

  useEffect(() => {
    const isAndroidCapacitor = typeof window !== "undefined" && /Android/i.test(navigator.userAgent) && /Capacitor/i.test(navigator.userAgent);
    const isCapacitor = typeof window !== "undefined" && /Capacitor/i.test(navigator.userAgent);

    const checkAuth = async () => {
      if (typeof window !== "undefined") {
        try {
          const { sessionRestorePromise, hasStoredRefreshToken } = await import("@/lib/supabase/client");
          // Fast path: no token on web - skip wait, sessionRestorePromise resolves quickly anyway
          if (!isCapacitor && !hasStoredRefreshToken()) {
            await syncSession();
            const stateFast = useAuthStore.getState();
            if (stateFast.isAuthenticated && stateFast.user) {
              const cookieResult = await alignServerAuthCookiesBeforeAppEntry();
              if (cookieResult !== "ok") {
                logAuthDecision("Auth layout fast path: cookie-session check failed", {
                  cookieResult,
                  pathname,
                });
                await clearClientAuthIfServerSaysSignedOut(cookieResult, "auth_layout_fast_path_cookie");
              } else {
                const redirectUrl = getRedirectFromParamsOrStorage(
                  typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null,
                  "/dashboard"
                );
                clearRedirectUrl();
                router.replace(redirectUrl);
              }
            }
            return;
          }
          if (isAndroidCapacitor) {
            console.log("[Auth Layout] [Android Debug] Waiting for session restoration before auth check...");
          }
          await Promise.race([
            sessionRestorePromise,
            new Promise((resolve) => setTimeout(resolve, 6000)),
          ]);
          await new Promise((resolve) => setTimeout(resolve, isAndroidCapacitor ? 150 : 50));
        } catch (error) {
          console.warn("[Auth Layout] Session restoration wait failed (non-fatal):", error);
        }
      }
      
      console.log("[Auth Layout] [Android Debug] Syncing session...");
      await syncSession();
      
      const state = useAuthStore.getState();
      console.log("[Auth Layout] [Android Debug] Auth state after sync:", {
        isAuthenticated: state.isAuthenticated,
        hasUser: !!state.user,
        userId: state.user?.id?.substring(0, 8) + "...",
        pathname,
      });

      // Middleware uses cookies for protected routes; align before client-side navigation (native + web).
      if (state.isAuthenticated && state.user) {
        const cookieResult = await alignServerAuthCookiesBeforeAppEntry();
        if (cookieResult !== "ok") {
          logAuthDecision("Auth layout: cookie-session check failed", {
            cookieResult,
            pathname,
          });
          await clearClientAuthIfServerSaysSignedOut(cookieResult, "auth_layout_cookie_mismatch");
          return;
        }
      }
      
      // If user is authenticated, redirect from login/signup pages immediately
      // CRITICAL: Don't wait for profile - redirect immediately like social apps do
      // The app layout will handle profile loading and onboarding checks
      const stateAfterCookie = useAuthStore.getState();
      if (stateAfterCookie.isAuthenticated && stateAfterCookie.user) {
        if (pathname?.startsWith("/auth/login") || pathname?.startsWith("/auth/signup")) {
          // Get redirect URL from params or storage, default to dashboard
          const redirectUrl = getRedirectFromParamsOrStorage(
            typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null,
            "/dashboard"
          );
          clearRedirectUrl();
          router.replace(redirectUrl); // Use replace to avoid adding to history
        } else if (pathname?.startsWith("/auth/") && !pathname?.startsWith("/auth/set-password")) {
          // For other auth pages (callback, etc.), redirect if authenticated.
          // Do NOT redirect from /auth/set-password — user must set new password after recovery link.
          const redirectUrl = getRedirectFromParamsOrStorage(
            typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null,
            "/dashboard"
          );
          clearRedirectUrl();
          router.replace(redirectUrl);
        }
      }
    };
    
    checkAuth();
  }, [router, syncSession, pathname]);

  return (
    <div
      className="grid min-h-[100dvh] grid-rows-[auto_1fr] lg:grid-cols-[1fr_1fr] lg:grid-rows-1"
      style={{
        // Android: Ensure container extends to very top
        marginTop: isAndroidPlatform ? "0px" : undefined,
        paddingTop: isAndroidPlatform ? "0px" : undefined,
      }}
    >
      {/* Mobile header. The mark on the deep brand surface, nothing else: on a phone this
          strip exists to say where you are, and the panel's story would just push the form
          below the fold. */}
      <div
        className="lg:hidden flex items-center px-6 text-white flex-shrink-0"
        style={{
          background: "linear-gradient(150deg, var(--color-deep) 0%, var(--color-deep-soft) 100%)",
          // Android: Add safe-area-inset-top to prevent logo from being covered by status bar
          // iOS: Keep safe-area padding
          paddingTop: isAndroidPlatform
            ? "calc(env(safe-area-inset-top, 0px) + 1.25rem)"
            : "calc(var(--sat) + 1.25rem)",
          paddingBottom: "1.25rem",
          marginTop: "0px",
          height: isAndroidPlatform
            ? "calc(env(safe-area-inset-top, 0px) + 5rem)"
            : "calc(var(--sat) + 5rem)",
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        <Link href={homeHref} aria-label="GrowthSync home" className="inline-flex items-center">
          <Image
            src="/logo-full-white.webp"
            alt="GrowthSync"
            width={625}
            height={181}
            priority
            className="h-6 w-auto"
          />
        </Link>
      </div>

      {/* Desktop side panel. The deep surface carries the brand atmosphere and the logo sits
          straight on it in its white form, which is what that asset exists for. It used to be
          the blue wordmark on a white plate, which read as a sticker stuck onto the panel. The
          proof is a match card, the product's front door, not a slogan. */}
      <div
        className="hidden lg:flex flex-col p-12 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(160deg, var(--color-deep) 0%, var(--color-deep-soft) 100%)" }}
      >
        <div className="flex-shrink-0">
          <Link href={homeHref} aria-label="GrowthSync home" className="inline-flex items-center">
            <Image
              src="/logo-full-white.webp"
              alt="GrowthSync"
              width={625}
              height={181}
              priority
              className="h-8 w-auto"
            />
          </Link>
        </div>

        <div className="my-auto max-w-[34rem] py-14">
          <h1 className="gs-headline text-white">{t("auth.panelHeadline")}</h1>
          <p className="mt-5 max-w-[44ch] text-base leading-relaxed text-white/75">{t("auth.panelBody")}</p>

          {/* The product as proof: the swipe card a member sees, not an illustration of one. */}
          <div
            aria-hidden="true"
            className="mt-12 max-w-sm rounded-3xl bg-white p-5 text-neutral-900 shadow-[0_32px_70px_-30px_rgba(4,10,45,0.8)]"
          >
            <div className="flex items-center gap-3">
              <span
                className="grid h-11 w-11 place-items-center rounded-xl text-xs font-bold"
                style={{ background: "var(--color-primary-light)", color: "var(--color-primary-dark)" }}
              >
                {t("auth.panelCardInitials")}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{t("auth.panelCardRole")}</p>
                <p className="truncate text-xs text-neutral-500">{t("auth.panelCardMeta")}</p>
              </div>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-neutral-600">{t("auth.panelCardLine")}</p>
            <div className="mt-5 flex gap-2">
              <span className="flex-1 rounded-lg bg-neutral-100 py-2 text-center text-xs font-semibold text-neutral-500">
                {t("auth.panelCardSkip")}
              </span>
              <span
                className="flex-1 rounded-lg py-2 text-center text-xs font-semibold text-white"
                style={{ background: "var(--color-primary)" }}
              >
                {t("auth.panelCardSync")}
              </span>
            </div>
          </div>

          <ul className="mt-12 space-y-3 text-sm text-white/80">
            {[t("auth.panelPoint1"), t("auth.panelPoint2"), t("auth.panelPoint3")].map((point) => (
              <li key={point} className="flex items-center gap-3">
                <span
                  className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                  style={{ background: "var(--color-primary-vivid)" }}
                  aria-hidden="true"
                />
                {point}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-white/40 mt-auto pt-8">
          © {new Date().getFullYear()} GrowthSync. All rights reserved.
        </p>
      </div>

      {/* Main content area - scrollable on Android to prevent freeze when content overflows */}
      <div
        className="flex flex-col bg-[#fafafa] px-4 pt-6 pb-4 sm:px-6 lg:py-8 lg:px-20 min-h-0"
        style={{
          colorScheme: "light",
          ...(isAndroidPlatform
            ? { overflowY: "auto", maxHeight: "calc(100dvh - 6rem)", WebkitOverflowScrolling: "touch" }
            : {}),
        }}
      >
        <div className="mx-auto w-full flex flex-col flex-1 min-h-0 space-y-4 md:space-y-6" style={{ maxWidth: "1300px" }}>
          {children}
          {/* Reserves the consent banner's measured height while it is up, so it stops
              covering the Sign up, Terms, Privacy and Cookie links that sit at the bottom
              of this column. The banner is a fixed overlay and every other page scrolls
              out from under it; this one does not. --consent-banner-h is published on
              <html> by components/analytics-consent-banner.tsx and is 0 once answered,
              so this collapses to nothing the rest of the time. */}
          <div aria-hidden="true" className="shrink-0" style={{ height: "var(--consent-banner-h, 0px)" }} />
        </div>
      </div>
    </div>
  );
}
